import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '../ui/button';
import { FormField, AVCheckOptions } from '../../types/form';
import { Video, VideoOff, Mic, MicOff, CheckCircle2, XCircle, RefreshCw, Loader2 } from 'lucide-react';

interface AVCheckFieldProps {
  field: FormField;
  value: any;
  onChange: (value: any) => void;
}

// Rhety palette
const INK = '#26251e';
const ACCENT = '#f54e00';
const PAPER = '#faf9f6';

type Phase = 'idle' | 'requesting' | 'checking' | 'done' | 'error';

// Mic level above this normalized value (0..1) counts as a real signal (over noise floor).
const MIC_SIGNAL_THRESHOLD = 0.04;
// How long to sample the mic before deciding pass/fail.
const MIC_SAMPLE_MS = 2500;

const AVCheckField: React.FC<AVCheckFieldProps> = ({ field, value, onChange }) => {
  const options = (field.options || {}) as AVCheckOptions;

  const [phase, setPhase] = useState<Phase>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [micLevel, setMicLevel] = useState(0); // normalized 0..1 for the live meter
  const [cameraLive, setCameraLive] = useState(false);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const rafRef = useRef<number | null>(null);
  const peakRef = useRef(0); // peak normalized level observed during sampling

  // Full teardown: stop all tracks, close the AudioContext, cancel animation frame.
  const cleanup = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (sourceRef.current) {
      try {
        sourceRef.current.disconnect();
      } catch {
        /* noop */
      }
      sourceRef.current = null;
    }
    analyserRef.current = null;
    if (audioContextRef.current) {
      const ctx = audioContextRef.current;
      audioContextRef.current = null;
      if (ctx.state !== 'closed') {
        ctx.close().catch(() => {
          /* noop */
        });
      }
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraLive(false);
  }, []);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  const finish = useCallback(
    (cameraOk: boolean, micOk: boolean) => {
      const videoTrack = streamRef.current?.getVideoTracks()[0];
      const settings = videoTrack?.getSettings ? videoTrack.getSettings() : undefined;
      const width = settings?.width;
      const height = settings?.height;

      const camera: 'pass' | 'fail' = cameraOk ? 'pass' : 'fail';
      const mic: 'pass' | 'fail' = micOk ? 'pass' : 'fail';
      // When requireBoth is false, a single passing modality is enough.
      const requireBoth = options.requireBoth !== false;
      const overall = requireBoth ? cameraOk && micOk : cameraOk || micOk;
      const status: 'pass' | 'fail' = overall ? 'pass' : 'fail';

      onChange({
        camera,
        mic,
        status,
        width,
        height,
        timestamp: new Date().toISOString(),
      });

      setPhase('done');
      // Local preview only — release hardware once the check concludes.
      cleanup();
    },
    [cleanup, onChange, options.requireBoth]
  );

  const startCheck = useCallback(async () => {
    setErrorMessage('');
    setMicLevel(0);
    peakRef.current = 0;
    setPhase('requesting');

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setErrorMessage(
        'Your browser does not support camera and microphone access. Please try a modern browser such as Chrome, Firefox, or Safari.'
      );
      setPhase('error');
      return;
    }

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
    } catch (err: any) {
      const name = err?.name || '';
      if (name === 'NotAllowedError' || name === 'SecurityError' || name === 'PermissionDeniedError') {
        setErrorMessage(
          'Permission was denied. Please allow camera and microphone access in your browser (check the address bar), then retry.'
        );
      } else if (name === 'NotFoundError' || name === 'DevicesNotFoundError' || name === 'OverconstrainedError') {
        setErrorMessage(
          'No camera or microphone was found. Please connect a device and make sure no other app is using it, then retry.'
        );
      } else if (name === 'NotReadableError' || name === 'TrackStartError') {
        setErrorMessage(
          'Your camera or microphone could not be started. It may be in use by another application. Close it and retry.'
        );
      } else {
        setErrorMessage('Could not access your camera and microphone. Please retry.');
      }
      setPhase('error');
      return;
    }

    streamRef.current = stream;
    setPhase('checking');

    // Attach the (muted) live preview.
    if (videoRef.current) {
      videoRef.current.srcObject = stream;
      videoRef.current.play().catch(() => {
        /* autoplay may require the muted attr, which is set on the element */
      });
    }

    const videoTrack = stream.getVideoTracks()[0];
    const cameraOk = !!videoTrack && videoTrack.readyState === 'live';
    setCameraLive(cameraOk);

    // Set up the Web Audio mic level meter.
    let micMeasured = false;
    const audioTrack = stream.getAudioTracks()[0];
    if (audioTrack) {
      try {
        const AudioCtx: typeof AudioContext =
          (window as any).AudioContext || (window as any).webkitAudioContext;
        const ctx = new AudioCtx();
        audioContextRef.current = ctx;
        if (ctx.state === 'suspended') {
          ctx.resume().catch(() => {
            /* noop */
          });
        }
        const source = ctx.createMediaStreamSource(stream);
        sourceRef.current = source;
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 1024;
        analyserRef.current = analyser;
        source.connect(analyser);

        const data = new Uint8Array(analyser.fftSize);

        const sample = () => {
          const current = analyserRef.current;
          if (!current) return;
          current.getByteTimeDomainData(data);
          // RMS deviation from the 128 midpoint, normalized to 0..1.
          let sumSquares = 0;
          for (let i = 0; i < data.length; i += 1) {
            const centered = (data[i] - 128) / 128;
            sumSquares += centered * centered;
          }
          const rms = Math.sqrt(sumSquares / data.length);
          const level = Math.min(1, rms);
          if (level > peakRef.current) {
            peakRef.current = level;
          }
          setMicLevel(level);
          rafRef.current = requestAnimationFrame(sample);
        };
        micMeasured = true;
        rafRef.current = requestAnimationFrame(sample);
      } catch {
        micMeasured = false;
      }
    }

    // After the sampling window, decide pass/fail based on peak mic level.
    window.setTimeout(() => {
      // If the stream was already torn down (e.g. unmount), bail out.
      if (!streamRef.current) return;
      const micOk = micMeasured && peakRef.current >= MIC_SIGNAL_THRESHOLD;
      finish(cameraOk, micOk);
    }, MIC_SAMPLE_MS);
  }, [finish]);

  const result = phase === 'done' && value ? value : null;
  const checking = phase === 'checking' || phase === 'requesting';

  return (
    <div
      className="rounded-xl border p-4"
      style={{ borderColor: `${INK}1f`, backgroundColor: PAPER, color: INK }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Video className="w-5 h-5" style={{ color: ACCENT }} aria-hidden="true" />
          <span className="font-medium" style={{ color: INK }}>
            Camera &amp; microphone check
          </span>
        </div>
        {result && (
          <span
            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium"
            style={
              result.status === 'pass'
                ? { backgroundColor: '#16653414', color: '#166534' }
                : { backgroundColor: '#b9121214', color: '#b91212' }
            }
          >
            {result.status === 'pass' ? (
              <CheckCircle2 className="w-3.5 h-3.5" aria-hidden="true" />
            ) : (
              <XCircle className="w-3.5 h-3.5" aria-hidden="true" />
            )}
            {result.status === 'pass' ? 'Passed' : 'Failed'}
          </span>
        )}
      </div>

      <p className="mt-1 text-xs" style={{ color: `${INK}99` }}>
        This is a local preview only. Nothing is recorded or uploaded.
      </p>

      <p
        className="mt-2 rounded-md px-3 py-2 text-sm font-medium"
        style={{ backgroundColor: `${ACCENT}14`, color: ACCENT }}
      >
        PLEASE NOTE that during the camera and microphone check, you should speak
        so that the system can detect your audio.
      </p>

      <div className="mt-3 overflow-hidden rounded-lg bg-black">
        <video
          ref={videoRef}
          muted
          playsInline
          autoPlay
          className="h-48 w-full object-cover"
          style={{ display: checking || cameraLive ? 'block' : 'none' }}
        />
        {!checking && !cameraLive && (
          <div className="flex h-48 w-full items-center justify-center" style={{ color: `${PAPER}aa` }}>
            <VideoOff className="w-10 h-10" aria-hidden="true" />
          </div>
        )}
      </div>

      {/* Live mic level meter */}
      <div className="mt-3">
        <div className="mb-1 flex items-center gap-2 text-xs" style={{ color: `${INK}99` }}>
          {micLevel >= MIC_SIGNAL_THRESHOLD ? (
            <Mic className="w-4 h-4" style={{ color: ACCENT }} aria-hidden="true" />
          ) : (
            <MicOff className="w-4 h-4" aria-hidden="true" />
          )}
          <span>Microphone level</span>
        </div>
        <div className="h-2.5 w-full overflow-hidden rounded-full" style={{ backgroundColor: `${INK}14` }}>
          <div
            className="h-full rounded-full transition-[width] duration-75"
            style={{
              width: `${Math.min(100, Math.round(micLevel * 140))}%`,
              backgroundColor: ACCENT,
            }}
          />
        </div>
      </div>

      {phase === 'error' && errorMessage && (
        <div
          className="mt-3 flex items-start gap-2 rounded-lg p-3 text-sm"
          style={{ backgroundColor: '#b9121210', color: '#b91212' }}
          role="alert"
        >
          <XCircle className="mt-0.5 w-4 h-4 flex-shrink-0" aria-hidden="true" />
          <span>{errorMessage}</span>
        </div>
      )}

      {result && (
        <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
          <div className="flex items-center gap-2">
            {result.camera === 'pass' ? (
              <CheckCircle2 className="w-4 h-4" style={{ color: '#166534' }} aria-hidden="true" />
            ) : (
              <XCircle className="w-4 h-4" style={{ color: '#b91212' }} aria-hidden="true" />
            )}
            <span style={{ color: INK }}>
              Camera{result.width && result.height ? ` (${result.width}×${result.height})` : ''}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {result.mic === 'pass' ? (
              <CheckCircle2 className="w-4 h-4" style={{ color: '#166534' }} aria-hidden="true" />
            ) : (
              <XCircle className="w-4 h-4" style={{ color: '#b91212' }} aria-hidden="true" />
            )}
            <span style={{ color: INK }}>Microphone</span>
          </div>
        </div>
      )}

      <div className="mt-4">
        {phase === 'idle' && (
          <Button
            type="button"
            onClick={startCheck}
            style={{ backgroundColor: ACCENT, color: PAPER }}
          >
            <Video className="mr-2 w-4 h-4" aria-hidden="true" />
            Start check
          </Button>
        )}

        {checking && (
          <Button type="button" disabled style={{ backgroundColor: ACCENT, color: PAPER }}>
            <Loader2 className="mr-2 w-4 h-4 animate-spin" aria-hidden="true" />
            {phase === 'requesting' ? 'Requesting access…' : 'Checking…'}
          </Button>
        )}

        {(phase === 'done' || phase === 'error') && (
          <Button
            type="button"
            variant="outline"
            onClick={startCheck}
            style={{ borderColor: `${INK}33`, color: INK }}
          >
            <RefreshCw className="mr-2 w-4 h-4" aria-hidden="true" />
            {phase === 'error' ? 'Retry' : 'Run again'}
          </Button>
        )}
      </div>
    </div>
  );
};

export default AVCheckField;
